import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Loader2, Check, Gift } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b7533cee-6cc1-4a41-9e88-4b20184fec89/artifacts/eixt3fs2_STM%20mobile%20logo.png';

const Signup = () => {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Password strength
  const passwordChecks = {
    length: password.length >= 6,
    hasNumber: /\d/.test(password),
    hasLetter: /[a-zA-Z]/.test(password)
  };

  const passwordStrength = Object.values(passwordChecks).filter(Boolean).length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const userData = await signup(email, password, referralCode);
      toast.success('Account created! Please check your email to verify.');
      // Redirect to check-email page instead of app
      navigate('/check-email', { state: { email: userData.email } });
    } catch (error) {
      const message = error.response?.data?.detail?.error || 'Signup failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background: 'linear-gradient(135deg, #0B0F1A 0%, #0E1022 50%, #12162B 100%)'
      }}
    >
      {/* Subtle background glow */}
      <div 
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(124, 92, 255, 0.08) 0%, transparent 60%)'
        }}
      />

      <div className="w-full max-w-md relative z-10">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-4">
            <img 
              src={LOGO_URL} 
              alt="ScopeTheMarket" 
              className="h-10 w-auto"
              style={{ filter: 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.5))' }}
            />
            <span className="text-xl font-semibold" style={{ color: '#F4F4F5' }}>
              ScopeTheMarket
            </span>
          </Link>
          <h1 
            className="text-3xl font-bold mb-2"
            style={{ color: '#F4F4F5' }}
          >
            Create your account
          </h1>
          <p style={{ color: '#71717A' }}>
            Start tracking market anomalies today
          </p>
          
          {/* Referral Banner */}
          {referralCode && (
            <div 
              className="mt-4 px-4 py-3 rounded-xl flex items-center gap-3"
              style={{
                background: 'linear-gradient(135deg, rgba(124, 92, 255, 0.15) 0%, rgba(91, 140, 255, 0.1) 100%)',
                border: '1px solid rgba(124, 92, 255, 0.3)'
              }}
            >
              <Gift className="w-5 h-5" style={{ color: '#7C5CFF' }} />
              <span className="text-sm" style={{ color: '#A78BFA' }}>
                You were invited! Create your account to get started.
              </span>
            </div>
          )}
        </div>

        {/* Auth Card */}
        <div 
          className="rounded-2xl p-8"
          style={{
            background: 'rgba(20, 24, 40, 0.8)',
            border: '1px solid rgba(124, 92, 255, 0.15)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 80px rgba(124, 92, 255, 0.05)'
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <div>
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: '#A1A1AA' }}
              >
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl text-sm transition-all duration-200 outline-none"
                style={{
                  background: 'rgba(15, 18, 30, 0.8)',
                  border: '1px solid rgba(113, 113, 122, 0.2)',
                  color: '#F4F4F5'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(124, 92, 255, 0.5)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(124, 92, 255, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(113, 113, 122, 0.2)';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="you@example.com"
                data-testid="signup-email-input"
              />
            </div>

            {/* Password Input */}
            <div>
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: '#A1A1AA' }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-12 rounded-xl text-sm transition-all duration-200 outline-none"
                  style={{
                    background: 'rgba(15, 18, 30, 0.8)',
                    border: '1px solid rgba(113, 113, 122, 0.2)',
                    color: '#F4F4F5'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(124, 92, 255, 0.5)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(124, 92, 255, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(113, 113, 122, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="Create a password"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors"
                  style={{ color: '#71717A' }}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {password.length > 0 && (
                <div className="mt-3 space-y-2">
                  <div className="flex gap-1">
                    {[1, 2, 3].map((level) => (
                      <div 
                        key={level}
                        className="h-1 flex-1 rounded-full transition-colors"
                        style={{
                          background: passwordStrength >= level 
                            ? passwordStrength === 3 ? '#4ade80' : passwordStrength === 2 ? '#fbbf24' : '#f87171'
                            : 'rgba(113, 113, 122, 0.2)'
                        }}
                      />
                    ))}
                  </div>
                  <div className="space-y-1">
                    {[
                      { key: 'length', label: 'At least 6 characters' },
                      { key: 'hasLetter', label: 'Contains a letter' },
                      { key: 'hasNumber', label: 'Contains a number' }
                    ].map((check) => (
                      <div 
                        key={check.key}
                        className="flex items-center gap-2 text-xs"
                        style={{ color: passwordChecks[check.key] ? '#4ade80' : '#71717A' }}
                      >
                        <Check className={`w-3 h-3 ${passwordChecks[check.key] ? 'opacity-100' : 'opacity-30'}`} />
                        {check.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: '#A1A1AA' }}
              >
                Confirm Password
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl text-sm transition-all duration-200 outline-none"
                style={{
                  background: 'rgba(15, 18, 30, 0.8)',
                  border: `1px solid ${confirmPassword && confirmPassword !== password ? 'rgba(248, 113, 113, 0.5)' : 'rgba(113, 113, 122, 0.2)'}`,
                  color: '#F4F4F5'
                }}
                onFocus={(e) => {
                  if (confirmPassword !== password) {
                    e.target.style.borderColor = 'rgba(248, 113, 113, 0.5)';
                  } else {
                    e.target.style.borderColor = 'rgba(124, 92, 255, 0.5)';
                  }
                  e.target.style.boxShadow = '0 0 0 3px rgba(124, 92, 255, 0.1)';
                }}
                onBlur={(e) => {
                  if (confirmPassword && confirmPassword !== password) {
                    e.target.style.borderColor = 'rgba(248, 113, 113, 0.5)';
                  } else {
                    e.target.style.borderColor = 'rgba(113, 113, 122, 0.2)';
                  }
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="Confirm your password"
                data-testid="signup-confirm-password-input"
              />
              {confirmPassword && confirmPassword !== password && (
                <p className="mt-1 text-xs" style={{ color: '#f87171' }}>
                  Passwords do not match
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || passwordStrength < 2 || password !== confirmPassword}
              className="w-full py-3 rounded-xl text-white font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2"
              style={{
                background: (loading || passwordStrength < 2 || password !== confirmPassword)
                  ? 'rgba(124, 92, 255, 0.5)' 
                  : 'linear-gradient(135deg, #7C5CFF 0%, #6B4FE0 100%)',
                boxShadow: (loading || passwordStrength < 2 || password !== confirmPassword)
                  ? 'none' 
                  : '0 4px 20px rgba(124, 92, 255, 0.3), 0 0 40px rgba(124, 92, 255, 0.1)',
                cursor: (loading || passwordStrength < 2 || password !== confirmPassword) ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!loading && passwordStrength >= 2 && password === confirmPassword) {
                  e.target.style.boxShadow = '0 4px 25px rgba(124, 92, 255, 0.4), 0 0 60px rgba(124, 92, 255, 0.15)';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && passwordStrength >= 2 && password === confirmPassword) {
                  e.target.style.boxShadow = '0 4px 20px rgba(124, 92, 255, 0.3), 0 0 40px rgba(124, 92, 255, 0.1)';
                  e.target.style.transform = 'translateY(0)';
                }
              }}
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create account'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full" style={{ borderTop: '1px solid rgba(113, 113, 122, 0.2)' }} />
            </div>
            <div className="relative flex justify-center">
              <span 
                className="px-3 text-xs"
                style={{ 
                  background: 'rgba(20, 24, 40, 0.8)',
                  color: '#71717A' 
                }}
              >
                Already have an account?
              </span>
            </div>
          </div>

          {/* Login Link */}
          <Link
            to="/login"
            className="block w-full py-3 rounded-xl text-center font-medium text-sm transition-all duration-200"
            style={{
              border: '1px solid rgba(124, 92, 255, 0.3)',
              color: '#A78BFA'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = 'rgba(124, 92, 255, 0.5)';
              e.target.style.background = 'rgba(124, 92, 255, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = 'rgba(124, 92, 255, 0.3)';
              e.target.style.background = 'transparent';
            }}
            data-testid="login-link"
          >
            Sign in instead
          </Link>
        </div>

        {/* Footer */}
        <p 
          className="text-center mt-6 text-xs"
          style={{ color: '#52525B' }}
        >
          By creating an account, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Signup;
